// The people behind a business, each with everything known about them AND the
// state of their identity check, in one place.
//
// These lived apart: the application's person data sat in a wall of rows on the
// Applicant tab, while the identity table showed a name, a role and a chip. A
// reviewer checking whether the person who passed the check is the person the
// application declares had to hold two screens in their head. Here each person
// is one card carrying both.

import { Alert, Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import type { VerificationParty } from '../../../api/customers.api.js';

import { Field, asArray, asRecord, asString, asStringArray } from './shared.js';

interface Props {
  readonly snapshot: Record<string, unknown>;
  readonly parties: readonly VerificationParty[];
  /** Rendered under each person: their identity check actions. */
  readonly renderPartyActions?: (party: VerificationParty) => JSX.Element | null;
}

/**
 * The declared people, however the application recorded them. Applications
 * captured before the single `people` list are folded in so historic cases stay
 * reviewable.
 */
export function collectPeople(snapshot: Record<string, unknown>): Record<string, unknown>[] {
  const business = asRecord(snapshot['business']);
  const people = asArray(business['people']);
  if (people.length > 0) return people;
  return [
    ...asArray(business['directors']).map((d) => ({
      ...asRecord(d),
      positions: [asString(asRecord(d)['role']) || 'DIRECTOR'],
    })),
    ...asArray(business['ubos']).map((u) => ({ ...asRecord(u), isUbo: true })),
  ];
}

/** Match a declared person to the provider party representing them, by name. */
function partyFor(
  person: Record<string, unknown>,
  parties: readonly VerificationParty[],
): VerificationParty | undefined {
  const full = `${asString(person['firstName'])} ${asString(person['lastName'])}`
    .trim()
    .toLowerCase();
  const declared = (asString(person['fullName']) || full).replace(/\s+/g, ' ').trim().toLowerCase();
  if (declared === '') return undefined;
  return parties.find(
    (party) => party.fullName.replace(/\s+/g, ' ').trim().toLowerCase() === declared,
  );
}

function decisionColour(decision: string): 'success' | 'error' | 'warning' | 'default' {
  if (decision === 'APPROVED') return 'success';
  if (decision === 'REJECTED') return 'error';
  if (decision === 'EDD_REQUIRED') return 'warning';
  return 'default';
}

export function PeopleSection({ snapshot, parties, renderPartyActions }: Props): JSX.Element {
  const people = collectPeople(snapshot);

  // A party the application does not declare is still the reviewer's problem —
  // it means the provider holds someone the file does not explain.
  const matched = new Set(
    people.map((person) => partyFor(person, parties)?.providerId).filter((id) => id !== undefined),
  );
  const unmatched = parties.filter((party) => !matched.has(party.providerId));

  if (people.length === 0 && parties.length === 0) {
    return (
      <Alert severity="info">
        No people are declared on this application and none are registered at the identity
        provider.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {people.map((person, index) => {
        const party = partyFor(person, parties);
        const decision = party?.identityDecision ?? '';
        // The same keys the application writes — inventing names here is how
        // every row came out as an em dash.
        const positions = [
          ...asStringArray(person['positions']),
          person['isShareholder'] === true ? 'SHAREHOLDER' : '',
          person['isUbo'] === true ? 'UBO' : '',
        ].filter((role) => role !== '');
        const declared = [
          person['isUsPerson'] === true ? 'US person' : '',
          person['isPep'] === true ? 'PEP' : '',
          person['hasCriminalRecord'] === true ? 'Criminal record' : '',
        ].filter((flag) => flag !== '');
        const held = Number(person['shareholdingPercent'] ?? person['ownershipPercent'] ?? 0);
        const documentDates = [
          asString(person['documentIssuedOn']),
          asString(person['documentExpiresOn']),
        ]
          .filter((value) => value !== '')
          .join(' — ');
        const livingAddress = [
          asString(person['livingAddress']),
          asString(person['livingCity']),
          asString(person['livingPostalCode']),
        ]
          .filter((value) => value !== '')
          .join(', ');
        const isCompany = asString(person['entityType']) === 'COMPANY';
        const name =
          (isCompany
            ? asString(person['companyName'])
            : `${asString(person['firstName'])} ${asString(person['lastName'])}`.trim()) ||
          asString(person['fullName']) ||
          'Unnamed person';

        return (
          <Paper key={`${name}-${String(index)}`} variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                {name}
              </Typography>
              {positions.map((position) => (
                <Chip key={position} size="small" label={position} />
              ))}
              {party === undefined ? (
                <Chip size="small" color="warning" label="Not at the provider" />
              ) : (
                <Chip
                  size="small"
                  color={decisionColour(decision)}
                  label={`Identity: ${decision === '' ? 'PENDING' : decision}`}
                  title={party.identityStatus}
                />
              )}
            </Stack>

            <Box sx={{ maxWidth: 820 }}>
              <Field label="Roles" value={positions.join(', ')} />
              {declared.length > 0 ? (
                <Field label="Declared" value={declared.join(' · ')} />
              ) : null}
              <Field label="Shareholding" value={held > 0 ? `${String(held)}%` : ''} />
              <Field
                label="Representative rights"
                value={asString(person['representativeRights'])}
              />
              <Field
                label="Date of birth"
                value={asString(person['dateOfBirth'])}
                path={`business.people.${String(index)}.dateOfBirth`}
              />
              <Field
                label="Passport number"
                value={asString(person['passportNumber'])}
                path={`business.people.${String(index)}.passportNumber`}
              />
              <Field
                label="Identity code"
                value={asString(person['identityCode'])}
                path={`business.people.${String(index)}.identityCode`}
              />
              <Field label="Document issued / expires" value={documentDates} />
              <Field
                label="Citizenship"
                value={asString(person['citizenship'])}
                path={`business.people.${String(index)}.citizenship`}
              />
              <Field
                label="Residence"
                value={asString(person['residence'])}
                path={`business.people.${String(index)}.residence`}
              />
              <Field label="Living address" value={livingAddress} />
              <Field
                label="Telephone"
                value={asString(person['phone'])}
                path={`business.people.${String(index)}.phone`}
              />
              <Field
                label="E-mail"
                value={asString(person['email'])}
                path={`business.people.${String(index)}.email`}
              />
            </Box>

            {party !== undefined && renderPartyActions !== undefined ? (
              <>
                <Divider sx={{ my: 1.5 }} />
                {renderPartyActions(party)}
              </>
            ) : null}
          </Paper>
        );
      })}

      {unmatched.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {String(unmatched.length)} person(s) exist at the identity provider that this
            application does not declare. Check whether they were added by hand or belong to an
            earlier version of the application.
          </Alert>
          {unmatched.map((party) => (
            <Stack
              key={party.providerId}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ py: 0.5 }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {party.fullName} · {party.role}
              </Typography>
              <Chip
                size="small"
                color={decisionColour(party.identityDecision)}
                label={party.identityDecision === '' ? 'PENDING' : party.identityDecision}
              />
              {renderPartyActions?.(party)}
            </Stack>
          ))}
        </Paper>
      ) : null}
    </Stack>
  );
}
